// ============================================================
// Expenses API Layer
// ============================================================

import client from './axios';

export const expensesApi = {
  // ── Expense Categories ──────────────────────────────────────
  /**
   * Get all expense categories
   * GET /expenses/categories?school_id=UUID&include_inactive=false
   */
  getCategories: async (schoolId, includeInactive = false) => {
    const response = await client.get('/expenses/categories', {
      params: { school_id: schoolId, include_inactive: includeInactive },
    });
    return response.data;
  },

  /**
   * Create new expense category
   * POST /expenses/categories?school_id=UUID
   * Body: { name: string }
   */
  createCategory: async (schoolId, payload) => {
    const response = await client.post('/expenses/categories', payload, {
      params: { school_id: schoolId },
    });
    return response.data;
  },

  /**
   * Update expense category
   * PUT /expenses/categories/{id}?school_id=UUID
   * Body: { name: string, is_active: boolean }
   */
  updateCategory: async (schoolId, id, payload) => {
    const response = await client.put(`/expenses/categories/${id}`, payload, {
      params: { school_id: schoolId },
    });
    return response.data;
  },

  /**
   * Delete expense category
   * DELETE /expenses/categories/{id}?school_id=UUID
   */
  deleteCategory: async (schoolId, id) => {
    const response = await client.delete(`/expenses/categories/${id}`, {
      params: { school_id: schoolId },
    });
    return response.data;
  },

  // ── Expenses ────────────────────────────────────────────────
  /**
   * Get list of expenses with optional filtering
   * GET /expenses?school_id=UUID&start_date=YYYY-MM-DD&end_date=YYYY-MM-DD&category_id=UUID
   */
  getExpenses: async (schoolId, params = {}) => {
    const cleanParams = { school_id: schoolId };
    if (params.start_date) cleanParams.start_date = params.start_date;
    if (params.end_date) cleanParams.end_date = params.end_date;
    if (params.category_id && params.category_id !== 'ALL') cleanParams.category_id = params.category_id;

    const response = await client.get('/expenses', { params: cleanParams });
    return response.data;
  },

  /**
   * Get single expense by ID
   * GET /expenses/{id}?school_id=UUID
   */
  getExpenseById: async (schoolId, id) => {
    const response = await client.get(`/expenses/${id}`, {
      params: { school_id: schoolId },
    });
    return response.data;
  },

  /**
   * Create new expense record
   * POST /expenses?school_id=UUID
   * Body: { expense_date, category_id, amount, description, receipt_url }
   */
  createExpense: async (schoolId, payload) => {
    const response = await client.post('/expenses', payload, {
      params: { school_id: schoolId },
    });
    return response.data;
  },

  /**
   * Update existing expense record
   * PUT /expenses/{id}?school_id=UUID
   * Body: { expense_date, category_id, amount, description, receipt_url }
   */
  updateExpense: async (schoolId, id, payload) => {
    const response = await client.put(`/expenses/${id}`, payload, {
      params: { school_id: schoolId },
    });
    return response.data;
  },

  /**
   * Delete expense record
   * DELETE /expenses/{id}?school_id=UUID
   */
  deleteExpense: async (schoolId, id) => {
    const response = await client.delete(`/expenses/${id}`, {
      params: { school_id: schoolId },
    });
    return response.data;
  },

  /**
   * Get total expense summary
   * GET /expenses/summary/total?school_id=UUID&start_date=YYYY-MM-DD&end_date=YYYY-MM-DD
   */
  getSummaryTotal: async (schoolId, params = {}) => {
    const cleanParams = { school_id: schoolId };
    if (params.start_date) cleanParams.start_date = params.start_date;
    if (params.end_date) cleanParams.end_date = params.end_date;

    const response = await client.get('/expenses/summary/total', { params: cleanParams });
    return response.data;
  },

  /**
   * Get expense dashboard overview analytics
   * GET /expense-dashboard/overview?school_id=UUID&from_date=YYYY-MM-DD&to_date=YYYY-MM-DD&category_id=UUID
   */
  getDashboardOverview: async (schoolId, params = {}) => {
    const cleanParams = { school_id: schoolId };
    if (params.from_date) cleanParams.from_date = params.from_date;
    if (params.to_date) cleanParams.to_date = params.to_date;
    if (params.category_id && params.category_id !== 'ALL') cleanParams.category_id = params.category_id;

    const response = await client.get('/expense-dashboard/overview', { params: cleanParams });
    return response.data;
  },
};
